import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import { MAX_VIEW_NAME_LENGTH, viewNameProblem } from "_shared/_views/list-pages/current-table-view";
import type { NamedTableViewJSON } from "_shared/_views/tables/_schemas/table-column-preferences-schema";
import { useId, useState, type FormEvent } from "react";

/**
 * Asks for the name of a new saved view (#6717). The name is checked here as the account checks it,
 * so the reader reads why beside the field rather than in a snackbar after the request.
 */
export function SaveViewDialog(
  props: Readonly<{
    open: boolean;
    views: readonly NamedTableViewJSON[];
    saving: boolean;
    onCancel: () => void;
    onSave: (name: string) => void;
  }>,
) {
  const { open, views, saving, onCancel, onSave } = props;
  const titleId = useId();
  const [name, setName] = useState("");
  const [touched, setTouched] = useState(false);
  const problem = viewNameProblem(name, views);

  function submit(event: FormEvent) {
    event.preventDefault();
    setTouched(true);
    if (problem !== undefined) return;
    onSave(name.trim());
  }

  function close() {
    setName("");
    setTouched(false);
    onCancel();
  }

  return (
    <Dialog
      aria-labelledby={titleId}
      fullWidth
      maxWidth="xs"
      open={open}
      onClose={close}
      slotProps={{ paper: { component: "form", onSubmit: submit } as object }}
    >
      <DialogTitle id={titleId}>Save as new view</DialogTitle>
      <DialogContent>
        <TextField
          error={touched && problem !== undefined}
          fullWidth
          helperText={touched ? problem : "Saves what the list shows now: tab, chips, filters, sort and columns."}
          label="View name"
          margin="dense"
          onChange={(event) => setName(event.target.value)}
          slotProps={{ htmlInput: { maxLength: MAX_VIEW_NAME_LENGTH + 20 } }}
          value={name}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button disabled={saving} type="submit" variant="contained">
          Save view
        </Button>
      </DialogActions>
    </Dialog>
  );
}
